/**
 * ModelForm — shared create/edit form for Model entities.
 *
 * Create flow: user picks a base model from the catalog dropdown (sourced
 * from `/api/proxy-models`) OR chooses "Custom endpoint" and points the
 * alias at a deployed model (endpoint URL + provider model name), served
 * through a private gateway upstream (e.g. `baseten`). Edit flow: one
 * Backing picker moves the model between catalog models and the team's
 * own deployment in any direction (TRA-1383); the rules for what the save
 * sends live in `backing.ts`.
 *
 * The slug regex matches the backend constraint
 * (`^[a-z0-9]([a-z0-9.-]{0,62}[a-z0-9])?$`) — no `/` allowed (those are
 * reserved for catalog slugs like `anthropic/sonnet-4-6`).
 */

import { useEffect, useRef, useState, type FormEvent } from 'react'
import { ChevronsUpDown, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCreateModel,
  useFallbackOptions,
  useProxyModels,
  useUpdateModel,
  type Model,
  type ModelScope,
} from '@/hooks/useModels'
import { AliasesSection } from '@/components/models/AliasesSection'
import {
  COPY_SCALING_LABEL,
  CUSTOM_BACKING,
  backingPatch,
  canCopyScaling,
  currentBacking,
  effectivePrimaryFor,
  isSameDeployment,
  modelUpdateBody,
  scalingCopyNotice,
  type UpstreamKind,
} from '@/components/models/backing'
import {
  billingLine,
  billingWarning,
  billingWarningCopy,
  gpuBillingLine,
  type BackingPlace,
} from '@/components/models/billing'
import { FallbackChainEditor } from '@/components/models/FallbackChainEditor'
import { endpointUrlHint, isEndpointUrlAllowed } from '@/components/models/endpointUrl'
import type { CustomEndpointCreate, ForwardsTo, ScalingCopyResult } from '@/api/generated/model'
import { dropPrimaryFromChain, validateFallbackChain } from '@/components/models/fallbackChain'
import { DEPLOYMENT } from '@/config'
import { SLUG_REGEX } from '@/lib/slug'

// Sentinel value for the "Custom endpoint" choice in the base-model picker.
const CUSTOM = '__custom__'

// TrainLoop-operated shared fleets a custom endpoint can authenticate
// through. Always pickable: create mode has no model id for the options
// endpoint, and in edit mode the live list only names upstreams the team
// already touches (tenant isolation), which would otherwise hide these
// from a team graduating onto its first private deployment.
// `healthCheckType` mirrors the gateway upstream's healthCheck.type: a
// baseten-probed upstream requires Baseten ids on every slot (TRA-1154).
interface UpstreamOption extends UpstreamKind {
  label: string
}
const PRIVATE_UPSTREAMS: UpstreamOption[] = [
  { id: 'baseten', label: 'Baseten', healthCheckType: 'baseten' },
  { id: 'wandb-inference', label: 'W&B', healthCheckType: null },
]

// Baseten endpoint hosts look like model-<id>.api.baseten.co; the model id
// is the same one shown on the deployment page.
function basetenModelIdFromUrl(baseUrl: string): string {
  const match = /^https:\/\/model-([a-z0-9]+)\.api\.baseten\.co\//.exec(baseUrl)
  return match?.[1] ?? ''
}

/** The save moved the model; say whether its scaling came along (TRA-1422). */
function announceScalingCopy(result: ScalingCopyResult): void {
  const notice = scalingCopyNotice(result)
  if (notice.status === 'copied') toast.success(notice.text)
  else toast.error(notice.text)
}

interface FormState {
  proxy_model_id: string
  custom_upstream_id: string
  custom_base_url: string
  custom_provider_model: string
  custom_baseten_model_id: string
  custom_baseten_deployment_id: string
  slug: string
  display_name: string
}

interface ModelFormProps {
  mode: 'create' | 'edit'
  initial?: Model
  /** Whose model this is: the signed-in team, or a team staff configure. */
  scope: ModelScope
  onSaved: (model: Model) => void
  onCancel: () => void
}

export function ModelForm({
  mode,
  initial,
  scope,
  onSaved,
  onCancel,
}: ModelFormProps): React.JSX.Element {
  const proxyModels = useProxyModels()
  const createMutation = useCreateModel(scope)
  const updateMutation = useUpdateModel(scope)
  // Chains are configured on existing models only — a chain for an unrouted
  // slug never fires, so create mode has nothing to offer.
  const fallbackOptions = useFallbackOptions(mode === 'edit' ? initial?.id : undefined, scope)

  const [form, setForm] = useState<FormState>({
    proxy_model_id: initial?.proxy_model_id ?? '',
    custom_upstream_id: 'baseten',
    custom_base_url: '',
    custom_provider_model: '',
    custom_baseten_model_id: '',
    custom_baseten_deployment_id: '',
    slug: initial?.slug ?? '',
    display_name: initial?.display_name ?? '',
  })
  // The stored chain arrives with the fallback options (it lives in the
  // team's gateway config, not on the Model row); null = untouched in this
  // dialog, so the rendered chain tracks the server until the first edit.
  const [editedFallbacks, setEditedFallbacks] = useState<string[] | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Edit-mode Backing pick: a catalog slug or CUSTOM_BACKING; null = untouched,
  // so the picker shows the current backing until the first pick.
  const [editedBacking, setEditedBacking] = useState<string | null>(null)
  const [backingPickerOpen, setBackingPickerOpen] = useState(false)
  // Chain entries the last backing change dropped, named under the chain
  // editor until the next backing change or chain edit.
  const [removedForPrimary, setRemovedForPrimary] = useState<string[]>([])
  // The billing warning's checkbox: on by default, so a repoint off a
  // customer-account deployment (display-only entry) or off a per-hour GPU
  // deployment (hourly entry) bills at the new backing unless unticked.
  const [dropBillingEntry, setDropBillingEntry] = useState(true)
  // The scaling copy's checkbox (TRA-1422): on by default, so a Baseten
  // deployment moved to another one keeps its windows and replica range.
  const [copyScaling, setCopyScaling] = useState(true)

  const isPending = createMutation.isPending || updateMutation.isPending
  const slugValid = form.slug === '' || SLUG_REGEX.test(form.slug)
  const isCustom = form.proxy_model_id === CUSTOM
  // Where the primary route actually sends requests, from the gateway.
  const forwardsTo = mode === 'edit' ? fallbackOptions.data?.forwards_to : undefined
  const picked = editedBacking ?? (forwardsTo ? currentBacking(forwardsTo) : null)
  // Custom upstream choices: the shared fleets plus, in edit mode, any
  // other private upstream the team already touches (live from the API).
  const upstreamOptions: UpstreamOption[] =
    mode === 'edit' && fallbackOptions.data
      ? [
          ...PRIVATE_UPSTREAMS,
          ...fallbackOptions.data.upstreams
            .filter((u) => !PRIVATE_UPSTREAMS.some((p) => p.id === u.id))
            .map((u) => ({ id: u.id, label: u.name, healthCheckType: u.health_check_type })),
        ]
      : PRIVATE_UPSTREAMS
  const needsBasetenIds =
    upstreamOptions.find((u) => u.id === form.custom_upstream_id)?.healthCheckType === 'baseten'
  const basetenIdsValid =
    !needsBasetenIds ||
    (form.custom_baseten_model_id.length > 0 && form.custom_baseten_deployment_id.length > 0)
  const customValid =
    form.custom_upstream_id.length > 0 &&
    isEndpointUrlAllowed(form.custom_base_url, DEPLOYMENT) &&
    form.custom_provider_model.length > 0 &&
    basetenIdsValid
  const showCustomFields = mode === 'create' ? isCustom : picked === CUSTOM_BACKING
  const customFields: CustomEndpointCreate | null = customValid
    ? {
        upstream_id: form.custom_upstream_id,
        base_url: form.custom_base_url,
        provider_model: form.custom_provider_model,
        ...(needsBasetenIds
          ? {
              baseten_model_id: form.custom_baseten_model_id,
              baseten_deployment_id: form.custom_baseten_deployment_id,
            }
          : { baseten_model_id: null, baseten_deployment_id: null }),
      }
    : null
  // The backing fields of the save, if the pick changes the backing.
  const backingChange =
    picked !== null && forwardsTo ? backingPatch(picked, forwardsTo, customFields) : null
  // A model already on its own deployment opens with that deployment in the
  // custom fields, minus the Baseten ids the gateway does not report, so an
  // untouched deployment counts as valid: the save does not send it.
  const customUnchanged =
    forwardsTo !== undefined &&
    isSameDeployment(forwardsTo, {
      upstream_id: form.custom_upstream_id,
      base_url: form.custom_base_url,
      provider_model: form.custom_provider_model,
    })
  const backingValid =
    mode === 'edit'
      ? picked !== CUSTOM_BACKING || customValid || customUnchanged
      : isCustom
        ? customValid
        : form.proxy_model_id.length > 0
  // The chain validates against the primary the SAVE will leave in place:
  // the picked backing when this edit switches it, else the stored route.
  // This is what catches "repointing orphans/loops the chain" before the
  // gateway's post-merge 400 does.
  const storedPrimary = fallbackOptions.data?.primary ?? ''
  const effectivePrimary =
    picked === null
      ? storedPrimary
      : effectivePrimaryFor(picked, customValid ? form.custom_upstream_id : null, storedPrimary)
  const serverChain = fallbackOptions.data?.chain ?? []
  const fallbacks = editedFallbacks ?? serverChain
  const fallbacksDirty =
    mode === 'edit' &&
    editedFallbacks !== null &&
    JSON.stringify(editedFallbacks) !== JSON.stringify(serverChain)
  // Re-validate an untouched chain too when the backing changes: the
  // gateway checks the post-merge pair, so a repoint can invalidate a chain
  // the user never edited in this dialog.
  const fallbacksNeedValidation =
    fallbacksDirty || (backingChange !== null && fallbacks.length > 0)
  const fallbacksValid =
    !fallbacksNeedValidation ||
    (fallbackOptions.data !== undefined &&
      validateFallbackChain(fallbacks, fallbackOptions.data, effectivePrimary)
        .length === 0)
  const canSubmit =
    backingValid &&
    (mode === 'create' || fallbacksValid) &&
    form.slug.length > 0 &&
    form.display_name.length > 0 &&
    slugValid &&
    !isPending

  function update<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }))
    setSubmitError(null)
  }

  // The custom fields show the CURRENT deployment so the user edits what
  // is actually set instead of typing from memory. A catalog-backed model
  // forwards via a PUBLIC upstream, which is not a valid custom target: its
  // endpoint and provider model belong to that upstream too, so prefill all
  // three only when the picker actually offers the forwarded upstream.
  function seedCustomFields(current: ForwardsTo): void {
    const pickable = upstreamOptions.some((u) => u.id === current.upstream_id)
    if (!pickable || current.upstream_id === null) return
    const upstreamId = current.upstream_id
    setForm((f) => ({
      ...f,
      custom_base_url: f.custom_base_url || (current.endpoint_url ?? ''),
      custom_provider_model: f.custom_provider_model || (current.provider_model ?? ''),
      custom_upstream_id: upstreamId,
    }))
  }

  // A model opened on its own deployment has the custom fields visible from
  // the start, so they are seeded once the gateway data arrives (the read
  // is async, so this cannot be the initial state). Seeded once: a later
  // 60s refetch must not refill a field the user cleared on purpose.
  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current || !forwardsTo || currentBacking(forwardsTo) !== CUSTOM_BACKING) return
    seeded.current = true
    seedCustomFields(forwardsTo)
    // seedCustomFields reads upstreamOptions, which derives from the same read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forwardsTo])

  // A backing change can make a chain entry useless: the new primary itself,
  // or anything served by its upstream. Drop those entries for the user and
  // name them, instead of blocking the save until they remove them by hand
  // (TRA-1383). Keyed on the effective primary, so a plain chain edit never
  // triggers it, and the custom-deployment case fires once its fields are
  // complete. Picking the original backing again does not put the dropped
  // entries back; re-adding a fallback is one click in the editor.
  const seenPrimary = useRef<string | null>(null)
  useEffect(() => {
    if (!fallbackOptions.data || seenPrimary.current === effectivePrimary) return
    const isFirstLoad = seenPrimary.current === null
    seenPrimary.current = effectivePrimary
    if (isFirstLoad) return
    const dropped = dropPrimaryFromChain(fallbacks, effectivePrimary, fallbackOptions.data)
    if (dropped.removed.length > 0) setEditedFallbacks(dropped.chain)
    setRemovedForPrimary(dropped.removed)
  }, [effectivePrimary, fallbacks, fallbackOptions.data])

  function pickBacking(value: string): void {
    setEditedBacking(value)
    setBackingPickerOpen(false)
    setSubmitError(null)
    if (value === CUSTOM_BACKING && forwardsTo) seedCustomFields(forwardsTo)
  }

  // On edit, look up the existing catalog row for read-only display.
  const currentProxy =
    initial && proxyModels.data
      ? proxyModels.data.find((p) => p.id === initial.proxy_model_id)
      : null

  // What the Backing picker shows for the pick, and names in the save hint.
  const pickedCatalog = fallbackOptions.data?.catalog.find((c) => c.slug === picked)
  // Where the pick lands when it moves the model: the catalog model, or the
  // typed custom upstream once its fields are complete. A display-only
  // billing entry (a deployment on the customer's own account, TRA-1383) or
  // an hourly one (a GPU deployment, TRA-1422) must not follow the model
  // onto a backing it does not price.
  const nextBacking: BackingPlace | null =
    backingChange === null
      ? null
      : 'custom' in backingChange
        ? { upstream_id: backingChange.custom.upstream_id, catalog_slug: null }
        : pickedCatalog
          ? { upstream_id: pickedCatalog.upstream_id, catalog_slug: pickedCatalog.slug }
          : null
  const warning = billingWarning(
    fallbackOptions.data?.billing ?? null,
    forwardsTo ?? null,
    nextBacking,
    upstreamOptions,
  )
  const showCopyScaling =
    forwardsTo !== undefined && canCopyScaling(backingChange, forwardsTo, upstreamOptions)
  // What the save does to the team's GPU billing (TRA-1422), for a GPU-billed team.
  const gpuLine = fallbackOptions.data
    ? gpuBillingLine(fallbackOptions.data.gpu_model_ids, backingChange)
    : null
  const pickedLabel =
    picked === CUSTOM_BACKING
      ? 'Custom deployment'
      : pickedCatalog?.display_name ?? picked ?? 'Loading…'
  // Catalog entries by display name, bare upstream ids as they are.
  const removedNames = removedForPrimary.map(
    (value) => fallbackOptions.data?.catalog.find((c) => c.slug === value)?.display_name ?? value,
  )

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    if (!canSubmit) return

    if (mode === 'create') {
      const created = await createMutation.mutateAsync({
        data: {
          proxy_model_id: isCustom ? null : form.proxy_model_id,
          custom: isCustom ? customFields : null,
          slug: form.slug,
          display_name: form.display_name,
        },
      })
      onSaved(created)
      return
    }

    if (!initial) {
      setSubmitError('Cannot edit: model not loaded')
      return
    }
    const updated = await updateMutation.mutateAsync({
      modelId: initial.id,
      data: modelUpdateBody({
        slug: form.slug,
        display_name: form.display_name,
        backing: backingChange,
        fallbacks: fallbacksDirty ? fallbacks : null,
        dropBillingEntry: warning !== null && dropBillingEntry,
        copyScaling: showCopyScaling && copyScaling,
      }),
    })
    if (updated.scaling_copy) announceScalingCopy(updated.scaling_copy)
    onSaved(updated)
  }

  const mutationError = createMutation.error ?? updateMutation.error ?? null
  const mutationErrorMessage =
    mutationError instanceof Error ? mutationError.message : null

  return (
    <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="proxy-model">Base model</Label>
        {mode === 'edit' ? (
          <>
            <Input
              id="proxy-model"
              value={
                initial?.proxy_model_id == null
                  ? 'Gateway-managed (external deployment)'
                  : currentProxy
                    ? `${currentProxy.display_name} · ${currentProxy.slug}`
                    : (proxyModels.isLoading ? 'Loading…' : 'Unknown')
              }
              readOnly
              disabled
            />
            {forwardsTo && (
              <dl className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
                {(
                  [
                    ['Upstream', forwardsTo.upstream_id],
                    ['Catalog model', forwardsTo.catalog_slug],
                    ['Sent as', forwardsTo.provider_model],
                    ['Endpoint', forwardsTo.endpoint_url],
                  ] as const
                )
                  .filter(([, value]) => value !== null)
                  .map(([label, value]) => (
                    <div key={label} className="flex gap-2">
                      <dt className="w-28 shrink-0 text-muted-foreground">{label}</dt>
                      <dd className="break-all font-mono text-xs leading-5">{value}</dd>
                    </div>
                  ))}
              </dl>
            )}
          </>
        ) : (
          <Select
            value={form.proxy_model_id}
            onValueChange={(v) => { update('proxy_model_id', v) }}
            disabled={isPending || proxyModels.isLoading}
          >
            <SelectTrigger id="proxy-model" aria-label="Base model">
              <SelectValue placeholder="Choose a base model…" />
            </SelectTrigger>
            <SelectContent>
              {(proxyModels.data ?? []).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.display_name}
                  {/* opacity (not a muted color token) so the slug stays
                      readable when the focused item swaps to accent colors */}
                  <span className="ml-2 text-xs opacity-60">{p.slug}</span>
                </SelectItem>
              ))}
              <SelectItem value={CUSTOM}>
                Custom endpoint
                <span className="ml-2 text-xs opacity-60">your own deployed model URL</span>
              </SelectItem>
            </SelectContent>
          </Select>
        )}
        {mode === 'edit' && (
          <div className="space-y-2">
            <Label htmlFor="backing">Backing</Label>
            {/* modal: see the same note on the chain editor's picker. */}
            <Popover modal open={backingPickerOpen} onOpenChange={setBackingPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="backing"
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={backingPickerOpen}
                  aria-label="Backing"
                  disabled={isPending || picked === null}
                  className="w-full justify-between font-normal"
                >
                  {pickedLabel}
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
                <Command>
                  <CommandInput placeholder="Search catalog models…" />
                  <CommandList>
                    <CommandEmpty>No matching model.</CommandEmpty>
                    <CommandGroup heading="Catalog models">
                      {(fallbackOptions.data?.catalog ?? []).map((c) => (
                        <CommandItem
                          key={c.slug}
                          value={`${c.display_name} ${c.slug}`}
                          onSelect={() => { pickBacking(c.slug) }}
                        >
                          {c.display_name}
                          <span className="ml-2 font-mono text-xs opacity-60">{c.slug}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandGroup heading="Your own">
                      <CommandItem
                        value="Custom deployment"
                        onSelect={() => { pickBacking(CUSTOM_BACKING) }}
                      >
                        Custom deployment
                        <span className="ml-2 text-xs opacity-60">your own endpoint URL</span>
                      </CommandItem>
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {forwardsTo && picked !== null && picked !== currentBacking(forwardsTo) && (
              <p className="text-xs text-muted-foreground">
                Saving will point this model at{' '}
                {picked === CUSTOM_BACKING ? 'your custom deployment' : pickedLabel}.
              </p>
            )}
            {fallbackOptions.data && (
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Billing.</span>{' '}
                {billingLine(fallbackOptions.data.billing)}
              </p>
            )}
            {gpuLine !== null && (
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">GPU billing.</span> {gpuLine}
              </p>
            )}
            {backingChange !== null && (
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Latency alerts.</span> They start
                fresh for the new backing, after about 500 new requests.
              </p>
            )}
            {warning !== null && (
              <div className="space-y-2 rounded-md border p-3 text-sm">
                <p>{billingWarningCopy(warning).message}</p>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="drop-billing-entry"
                    checked={dropBillingEntry}
                    onCheckedChange={(checked) => { setDropBillingEntry(checked === true) }}
                    disabled={isPending}
                    className="mt-0.5"
                  />
                  <Label htmlFor="drop-billing-entry" className="text-sm font-normal leading-5">
                    {billingWarningCopy(warning).label}
                  </Label>
                </div>
              </div>
            )}
            {showCopyScaling && (
              <div className="flex items-start gap-2 rounded-md border p-3 text-sm">
                <Checkbox
                  id="copy-scaling"
                  checked={copyScaling}
                  onCheckedChange={(checked) => { setCopyScaling(checked === true) }}
                  disabled={isPending}
                  className="mt-0.5"
                />
                <Label htmlFor="copy-scaling" className="text-sm font-normal leading-5">
                  {COPY_SCALING_LABEL}
                </Label>
              </div>
            )}
          </div>
        )}
        {proxyModels.isError && mode === 'create' && (
          <p className="text-sm text-destructive">
            Could not load the model catalog. Try refreshing.
          </p>
        )}
      </div>

      {showCustomFields && (
        <>
          <div className="space-y-2">
            <Label htmlFor="custom-base-url">Endpoint URL</Label>
            <Input
              id="custom-base-url"
              value={form.custom_base_url}
              onChange={(e) => {
                const url = e.target.value
                update('custom_base_url', url)
                if (form.custom_baseten_model_id === '') {
                  update('custom_baseten_model_id', basetenModelIdFromUrl(url))
                }
              }}
              placeholder="https://model-abc123.api.baseten.co/environments/production/sync/v1"
              disabled={isPending}
              required
            />
            <p className="text-xs text-muted-foreground">{endpointUrlHint(DEPLOYMENT)}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom-provider-model">Provider model</Label>
            <Input
              id="custom-provider-model"
              value={form.custom_provider_model}
              onChange={(e) => { update('custom_provider_model', e.target.value) }}
              placeholder="qwen3-27b-merged-step500"
              disabled={isPending}
              required
            />
            <p className="text-xs text-muted-foreground">
              The `model` string the endpoint itself expects.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom-upstream">Upstream (auth)</Label>
            <Select
              value={form.custom_upstream_id}
              onValueChange={(v) => { update('custom_upstream_id', v) }}
              disabled={isPending}
            >
              <SelectTrigger id="custom-upstream">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {upstreamOptions.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Private gateway upstream whose API key authenticates the request.
            </p>
          </div>

          {/* The gateway does not report Baseten ids, so a model opened on its
              own deployment shows them empty. The browser must not block a
              save that leaves that deployment alone: the ids are required
              only once the deployment itself changes. */}
          {needsBasetenIds && (
            <>
              <div className="space-y-2">
                <Label htmlFor="custom-baseten-model-id">Baseten model id</Label>
                <Input
                  id="custom-baseten-model-id"
                  value={form.custom_baseten_model_id}
                  onChange={(e) => { update('custom_baseten_model_id', e.target.value) }}
                  placeholder="abc12xyz"
                  disabled={isPending}
                  required={!customUnchanged}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="custom-baseten-deployment-id">Baseten deployment id</Label>
                <Input
                  id="custom-baseten-deployment-id"
                  value={form.custom_baseten_deployment_id}
                  onChange={(e) => { update('custom_baseten_deployment_id', e.target.value) }}
                  placeholder="q1w2e3r"
                  disabled={isPending}
                  required={!customUnchanged}
                />
                <p className="text-xs text-muted-foreground">
                  Both ids are on the deployment page in Baseten.
                </p>
              </div>
            </>
          )}
        </>
      )}

      {/* In edit mode the primary id is edited in the Model ids list below. */}
      {mode === 'create' && (
      <div className="space-y-2">
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            value={form.slug}
            onChange={(e) => { update('slug', e.target.value) }}
            placeholder="my-model-v1"
            maxLength={64}
            disabled={isPending}
            aria-invalid={!slugValid}
            aria-describedby={slugValid ? 'slug-hint' : 'slug-error'}
            required
          />
          {!slugValid && (
            <p id="slug-error" className="text-sm text-destructive">
              Slug must be lowercase alnum + hyphens/dots, start and end alphanumeric, ≤64 chars.
            </p>
          )}
          <p id="slug-hint" className="text-xs text-muted-foreground">
            Team-private alias clients send to the gateway as the OpenAI `model` field.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="display-name">Display name</Label>
        <Input
          id="display-name"
          value={form.display_name}
          onChange={(e) => { update('display_name', e.target.value) }}
          placeholder="My Model V1"
          maxLength={120}
          disabled={isPending}
          required
        />
      </div>

      {/* Aliases are live gateway edits, not part of this form's save. */}
      {mode === 'edit' && initial && (
        <AliasesSection
          model={initial}
          scope={scope}
          disabled={isPending}
          slug={form.slug}
          slugValid={slugValid}
          onSlugChange={(v) => { update('slug', v) }}
        />
      )}

      {mode === 'edit' && (
        fallbackOptions.data ? (
          <>
            <FallbackChainEditor
              chain={fallbacks}
              onChange={(next) => {
                setEditedFallbacks(next)
                setRemovedForPrimary([])
                setSubmitError(null)
              }}
              options={fallbackOptions.data}
              effectivePrimary={effectivePrimary}
              disabled={isPending}
            />
            {removedNames.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Removed from fallbacks because it is now the primary: {removedNames.join(', ')}.
              </p>
            )}
          </>
        ) : fallbackOptions.isError ? (
          <p className="text-sm text-destructive">
            Could not load fallback targets. The chain can&apos;t be edited
            right now. Other fields still save.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Loading fallback targets…</p>
        )
      )}

      {mutationErrorMessage && (
        <p role="alert" className="text-sm text-destructive">
          {mutationErrorMessage}
        </p>
      )}
      {submitError && (
        <p role="alert" className="text-sm text-destructive">
          {submitError}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {mode === 'create' ? 'Create model' : 'Save changes'}
        </Button>
      </div>
    </form>
  )
}
